'use client';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';

import { cn } from '../../lib/cn';

/** Props di {@link Avatar}: nessuna prop custom, solo gli attributi HTML standard (className, ecc.) inoltrati al div radice. */
export interface AvatarProps extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {}

/**
 * Contenitore circolare per l'immagine profilo di un utente (o socio CACER).
 * Va usato insieme a {@link AvatarImage} e {@link AvatarFallback}: mostra l'immagine
 * se disponibile, altrimenti il fallback (es. iniziali).
 */
const Avatar = React.forwardRef<React.ElementRef<typeof AvatarPrimitive.Root>, AvatarProps>(
	({ className, ...props }, ref) => (
		<AvatarPrimitive.Root
			ref={ref}
			className={cn('relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full', className)}
			{...props}
		/>
	),
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

/** Props di {@link AvatarImage}: eredita gli attributi standard dell'elemento `<img>` (src, alt, ecc.). */
export interface AvatarImageProps extends React.ComponentPropsWithoutRef<
	typeof AvatarPrimitive.Image
> {}

/** Immagine profilo vera e propria; se il caricamento fallisce (o manca `src`) Radix mostra automaticamente {@link AvatarFallback}. */
const AvatarImage = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Image>,
	AvatarImageProps
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Image
		ref={ref}
		className={cn('aspect-square h-full w-full', className)}
		{...props}
	/>
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

/** Props di {@link AvatarFallback}: eredita gli attributi standard di un div. */
export interface AvatarFallbackProps extends React.ComponentPropsWithoutRef<
	typeof AvatarPrimitive.Fallback
> {}

/** Contenuto mostrato al posto dell'immagine quando questa manca o non carica (es. iniziali dell'utente). */
const AvatarFallback = React.forwardRef<
	React.ElementRef<typeof AvatarPrimitive.Fallback>,
	AvatarFallbackProps
>(({ className, ...props }, ref) => (
	<AvatarPrimitive.Fallback
		ref={ref}
		className={cn(
			'flex h-full w-full items-center justify-center rounded-full bg-muted',
			className,
		)}
		{...props}
	/>
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
